import { Loader } from '@googlemaps/js-api-loader';
import { getGoogleMapsClientConfig } from '@/utils/googleMapsConfig';
import type { LiberiaDirectionsResult, LiberiaDirectionStep } from '@/utils/liberiaApi';

type Waypoint =
  | string
  | { lat?: number; lng?: number; address?: string };

function waypointToDirectionsRequest(
  point: Waypoint
): string | google.maps.LatLngLiteral {
  if (typeof point === 'string') {
    return point;
  }
  if (Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng))) {
    return { lat: Number(point.lat), lng: Number(point.lng) };
  }
  return String(point.address || '').trim();
}

function stripHtml(html: string): string {
  return String(html || '')
    .replace(/<div[^>]*>/gi, ' ')
    .replace(/<\/div>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function travelModeToGoogle(mode: string): google.maps.TravelMode {
  const value = String(mode || 'driving').toUpperCase();
  if (value === 'WALKING') return google.maps.TravelMode.WALKING;
  if (value === 'TRANSIT') return google.maps.TravelMode.TRANSIT;
  if (value === 'BICYCLING') return google.maps.TravelMode.BICYCLING;
  return google.maps.TravelMode.DRIVING;
}

function buildMapsUrl(origin: Waypoint, destination: Waypoint, mode: string): string {
  const originText =
    typeof origin === 'string'
      ? origin
      : Number.isFinite(Number(origin.lat))
        ? `${origin.lat},${origin.lng}`
        : String(origin.address || '');
  const destText =
    typeof destination === 'string'
      ? destination
      : Number.isFinite(Number(destination.lat))
        ? `${destination.lat},${destination.lng}`
        : String(destination.address || '');
  const params = new URLSearchParams({
    api: '1',
    origin: originText,
    destination: destText,
    travelmode: mode,
  });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export async function getBrowserDirections({
  origin,
  destination,
  mode = 'driving',
}: {
  origin: Waypoint;
  destination: Waypoint;
  mode?: 'driving' | 'walking' | 'transit' | 'bicycling';
}): Promise<LiberiaDirectionsResult> {
  const { apiKey } = await getGoogleMapsClientConfig();
  const loader = new Loader({
    apiKey,
    version: 'weekly',
    libraries: ['places', 'marker'],
  });

  const payload = await withTimeout((async () => {
    await loader.load();
    await google.maps.importLibrary('routes');

    const service = new google.maps.DirectionsService();
    const request = {
      origin: waypointToDirectionsRequest(origin),
      destination: waypointToDirectionsRequest(destination),
      travelMode: travelModeToGoogle(mode),
      provideRouteAlternatives: false,
      unitSystem: google.maps.UnitSystem.METRIC,
    };

    return new Promise<google.maps.DirectionsResult>((resolve, reject) => {
      service.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result?.routes?.length) {
          resolve(result);
          return;
        }
        reject(new Error(status || 'Directions request failed'));
      });
    });
  })(), 20000, 'Timed out waiting for Maps turn-by-turn directions. Open the route in Google Maps instead.');

  const route = payload.routes[0];
  const legs = route?.legs || [];
  const steps: LiberiaDirectionStep[] = [];
  let distanceMeters = 0;
  let durationSeconds = 0;

  legs.forEach((leg) => {
    distanceMeters += Number(leg.distance?.value || 0);
    durationSeconds += Number(leg.duration?.value || 0);
    (leg.steps || []).forEach((step) => {
      steps.push({
        instruction: stripHtml(step.instructions || ''),
        distanceText: step.distance?.text || '',
        durationText: step.duration?.text || '',
        maneuver: (step as google.maps.DirectionsStep & { maneuver?: string }).maneuver || null,
        start: {
          lat: step.start_location.lat(),
          lng: step.start_location.lng(),
        },
        end: {
          lat: step.end_location.lat(),
          lng: step.end_location.lng(),
        },
      });
    });
  });

  const firstLeg = legs[0];
  const lastLeg = legs[legs.length - 1];
  const originLabel = firstLeg?.start_address || '';
  const destinationLabel = lastLeg?.end_address || '';

  return {
    status: steps.length > 0 ? 'OK' : 'ZERO_RESULTS',
    mode,
    originLabel,
    destinationLabel,
    distanceText: firstLeg?.distance?.text || '',
    durationText: firstLeg?.duration?.text || '',
    distanceMeters,
    durationSeconds,
    overviewPolyline: typeof route?.overview_polyline === 'string' ? route.overview_polyline : '',
    bounds: route?.bounds
      ? {
          northeast: {
            lat: route.bounds.getNorthEast().lat(),
            lng: route.bounds.getNorthEast().lng(),
          },
          southwest: {
            lat: route.bounds.getSouthWest().lat(),
            lng: route.bounds.getSouthWest().lng(),
          },
        }
      : null,
    steps,
    googleMapsUrl: buildMapsUrl(origin, destination, mode),
    crossBorder: Boolean(
      originLabel &&
        destinationLabel &&
        originLabel.split(',').pop()?.trim() !== destinationLabel.split(',').pop()?.trim()
    ),
  };
}
